import { isEqual, unionBy } from 'lodash/fp';
import { DateTime } from 'luxon';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { TableState } from 'dashboard/lib/interfaces/TableState';
import { Account } from 'dashboard/models/account';
import { Case } from 'dashboard/models/case';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplate } from 'dashboard/models/case-template';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import { CasesActions, CasesActionType, CasesFilterParams } from 'dashboard/redux/actions/cases';

// cases are mapped based on their request params
export type Cases = Record<string, Case[]>;

export const getInitialCasesFilters = (): CasesFilterParams => {
  const currentTime = DateTime.local();
  return {
    // Not deterministic initial state, but should be okay
    startDate: currentTime.minus({ weeks: 2 }).startOf('day').toISO(),
    // Need to supply end, otherwise will use single date range
    endDate: currentTime.endOf('day').toISO(),
    dateType: 'created_at',
    caseStateNames: [],
    caseTemplateIds: [],
    caseQueueIds: [],
    tagsConditions: [],
    assigneeIds: [],
    notAssigneeIds: [],
    teamAssigneeIds: [],
    omni: undefined,
    // use this flag to determine if we need to overwrite caseStateNames with
    // the case template initial state ids
    isInitial: true,
  };
};

export interface CasesState extends TableState<CasesFilterParams> {
  cases: Cases;
  accounts: Account[];
  caseQueues: CaseQueue[];
  users: User[];
  tags: Tag[];
  caseTemplates: CaseTemplate[];
  caseRequestKey?: string;
  allCaseTemplates: CaseTemplate[];
}

const initialCasesState: CasesState = {
  cases: {},
  accounts: [],
  caseQueues: [],
  users: [],
  tags: [],
  caseTemplates: [],
  allCaseTemplates: [],
  filters: getInitialCasesFilters(),
  sortParams: {
    sort: 'created_at',
    shouldSortDescending: true,
  },
  paginationParams: undefined,
  pageSize: DEFAULT_PAGE_SIZE,
  caseRequestKey: undefined,
  currentPage: 1,
};

export default (state: CasesState = initialCasesState, action: CasesActions) => {
  switch (action.type) {
    case CasesActionType.SetCases: {
      return {
        ...state,
        cases: {
          ...state.cases,
          [action.payload.key]: action.payload.cases,
        },
        paginationParams: action.payload.paginationParams,
      };
    }

    case CasesActionType.SetCasesRequestKey: {
      return {
        ...state,
        caseRequestKey: action.payload.key,
      };
    }

    case CasesActionType.SetCasesDetails: {
      return {
        ...state,
        accounts: unionBy('id', action.payload.accounts, state.accounts),
        caseQueues: unionBy('id', action.payload.caseQueues, state.caseQueues),
        users: unionBy('id', action.payload.users, state.users),
        tags: unionBy('id', action.payload.tags, state.tags),
        caseTemplates: unionBy('id', action.payload.caseTemplates, state.caseTemplates),
      };
    }

    case CasesActionType.SetAllCaseTemplates: {
      return {
        ...state,
        allCaseTemplates: unionBy('id', action.payload.caseTemplates, state.caseTemplates),
      };
    }

    case CasesActionType.SetCurrentPage: {
      return {
        ...state,
        currentPage: action.payload.currentPage,
      };
    }

    case CasesActionType.SetPageSize: {
      return {
        ...state,
        pageSize: action.payload.pageSize,
      };
    }

    case CasesActionType.SetFilters: {
      const newFilters = {
        ...state.filters,
        ...action.payload.filters,
      };

      if (isEqual(newFilters, state.filters)) {
        return state;
      } else {
        return {
          ...state,
          filters: newFilters,
        };
      }
    }

    case CasesActionType.SetSortParams: {
      return {
        ...state,
        sortParams: {
          ...action.payload.sortParams,
        },
      };
    }

    default:
      return state;
  }
};
